import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Paper,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import {
  getQueueIntegration,
  createQueueIntegration,
  updateQueueIntegration,
  testQueueIntegrationSession,
} from "../../services/queueIntegration.api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  btnLeft: {
    display: "flex",
    marginRight: "auto",
    marginLeft: 12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
}));

const getValidationSchema = (type) => {
  const baseSchema = {
    name: Yup.string()
      .required("El nombre es obligatorio")
      .test("not-empty", "El nombre es obligatorio", (value) => {
        return value !== null && value !== undefined && value !== "";
      })
      .test("not-whitespace", "El nombre no puede contener solo espacios", (value) => {
        if (!value) return false;
        return value.trim().length > 0;
      })
      .test("min-length", "El nombre debe tener al menos 2 caracteres", (value) => {
        if (!value) return false;
        return value.trim().length >= 2;
      })
      .test("max-length", "El nombre no puede exceder 50 caracteres", (value) => {
        if (!value) return true;
        return value.trim().length <= 50;
      }),
  };

  if (type === "dialogflow") {
    return Yup.object().shape({
      ...baseSchema,
      language: Yup.string()
        .min(2, "El idioma es obligatorio")
        .required("El idioma es obligatorio"),
      projectName: Yup.string()
        .min(3, "El nombre del proyecto debe tener al menos 3 caracteres")
        .max(100, "El nombre del proyecto no puede exceder 100 caracteres")
        .required("El nombre del proyecto es obligatorio"),
      jsonContent: Yup.string()
        .min(3, "El contenido JSON debe tener al menos 3 caracteres")
        .required("El contenido JSON es obligatorio"),
    });
  }

  if (type === "n8n" || type === "webhook") {
    return Yup.object().shape({
      ...baseSchema,
      urlN8N: Yup.string()
        .required("La URL es obligatoria")
        .test("is-valid-url", "Debe ser una URL válida", (value) => {
          if (!value) return false;
          const trimmedValue = value.trim();
          if (trimmedValue === "") return false;
          // Validar URL de forma más flexible (permite localhost, IPs, etc.)
          try {
            const url = new URL(trimmedValue);
            return url.protocol === "http:" || url.protocol === "https:";
          } catch {
            return false;
          }
        }),
    });
  }

  if (type === "typebot") {
    return Yup.object().shape({
      ...baseSchema,
      urlN8N: Yup.string()
        .required("La URL es obligatoria")
        .test("is-valid-url", "Debe ser una URL válida", (value) => {
          if (!value) return false;
          const trimmedValue = value.trim();
          if (trimmedValue === "") return false;
          // Validar URL de forma más flexible (permite localhost, IPs, etc.)
          try {
            const url = new URL(trimmedValue);
            return url.protocol === "http:" || url.protocol === "https:";
          } catch {
            return false;
          }
        }),
      typebotSlug: Yup.string()
        .required("El slug de Typebot es obligatorio")
        .min(1, "El slug es obligatorio"),
    });
  }

  return Yup.object().shape(baseSchema);
};



const QueueIntegration = ({ open, onClose, integrationId }) => {
  const classes = useStyles();

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",

  };

  const [integration, setIntegration] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadedId, setLastLoadedId] = useState(null);

  useEffect(() => {
    // Solo cargar datos cuando el modal está abierto
    if (!open) {
      // Resetear cuando el modal se cierra
      setLastLoadedId(null);
      return;
    }

    if (!integrationId) {
      setIntegration(initialState);
      setIsLoading(false);
      setLastLoadedId(null);
      return;
    }

    // Solo cargar si es un integrationId diferente al último cargado
    if (lastLoadedId !== integrationId) {
      setIsLoading(true);
      (async () => {
        try {
          const { data } = await getQueueIntegration(integrationId);
          setIntegration((prevState) => {
            return { ...prevState, ...data };
          });
          setLastLoadedId(integrationId);
        } catch (err) {
          toastError(err);
        } finally {
          setIsLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, open]);

  const handleClose = () => {
    onClose();
    // Resetear después de que el modal se cierre
    setTimeout(() => {
      setIntegration(initialState);
      setIsLoading(false);
      setLastLoadedId(null);
    }, 100);
  };

  const handleTestSession = async (event, values) => {
    try {
      const { projectName, jsonContent, language } = values;

      await testQueueIntegrationSession({
        projectName,
        jsonContent,
        language,
      });

      toast.success(i18n.t("queueIntegrationModal.messages.testSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDialogflow = async (values) => {
    try {
      if (values.type === 'n8n' || values.type === 'webhook' || values.type === 'typebot') values.projectName = values.name
      
      // Normalizar valores antes de enviar
      const trimmedValues = {
        ...values,
        name: values.name ? values.name.trim() : values.name
      };
      
      // Normalizar URL: trimear espacios en blanco
      if (trimmedValues.urlN8N) {
        trimmedValues.urlN8N = trimmedValues.urlN8N.trim();
      }

      // Limpiar campos de Typebot si el tipo no es typebot
      if (values.type !== "typebot") {
        delete trimmedValues.typebotExpires;
        delete trimmedValues.typebotDelayMessage;
        delete trimmedValues.typebotKeywordFinish;
        delete trimmedValues.typebotUnknownMessage;
        delete trimmedValues.typebotKeywordRestart;
        delete trimmedValues.typebotRestartMessage;
        delete trimmedValues.typebotSlug;
      }

      // ✅ CORRECCIÓN: Normalizar campos opcionales de typebot
      // ✅ IMPORTANTE: Enviar TODOS los campos (incluso vacíos) para que el backend los actualice
      if (values.type === "typebot") {
        // Para números: convertir de string a número si tiene valor, enviar 0 si está vacío
        if (trimmedValues.typebotExpires === "" || trimmedValues.typebotExpires === null || trimmedValues.typebotExpires === undefined) {
          trimmedValues.typebotExpires = 0; // ✅ Enviar 0 en lugar de eliminar
        } else {
          // Convertir string a número (ej: "10" -> 10)
          const expiresNum = Number(trimmedValues.typebotExpires);
          if (!isNaN(expiresNum)) {
            trimmedValues.typebotExpires = expiresNum;
          } else {
            trimmedValues.typebotExpires = 0; // ✅ Enviar 0 si no es un número válido
          }
        }
        if (trimmedValues.typebotDelayMessage === "" || trimmedValues.typebotDelayMessage === null || trimmedValues.typebotDelayMessage === undefined) {
          trimmedValues.typebotDelayMessage = 1000; // ✅ Enviar valor por defecto en lugar de eliminar
        } else {
          // Convertir string a número (ej: "1000" -> 1000)
          const delayNum = Number(trimmedValues.typebotDelayMessage);
          if (!isNaN(delayNum)) {
            trimmedValues.typebotDelayMessage = delayNum;
          } else {
            trimmedValues.typebotDelayMessage = 1000; // ✅ Enviar valor por defecto si no es un número válido
          }
        }
        
        // ✅ Para strings: trimear y enviar string vacío si está vacío (NO eliminar)
        // ✅ Esto permite que el backend actualice los campos a valores vacíos
        trimmedValues.typebotKeywordFinish = trimmedValues.typebotKeywordFinish ? trimmedValues.typebotKeywordFinish.trim() : "";
        trimmedValues.typebotUnknownMessage = trimmedValues.typebotUnknownMessage ? trimmedValues.typebotUnknownMessage.trim() : "";
        trimmedValues.typebotKeywordRestart = trimmedValues.typebotKeywordRestart ? trimmedValues.typebotKeywordRestart.trim() : "";
        trimmedValues.typebotRestartMessage = trimmedValues.typebotRestartMessage ? trimmedValues.typebotRestartMessage.trim() : "";
      }

      if (integrationId) {
        const { data } = await updateQueueIntegration(integrationId, trimmedValues);
        setIntegration(prevState => ({ ...prevState, ...data })); // Actualizar el estado del modal con los datos guardados
        toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
        // ✅ NO resetear el estado después de editar, mantener los datos guardados
        // Cerrar el modal
        onClose();
      } else {
        const { data } = await createQueueIntegration(trimmedValues);
        setIntegration(prevState => ({ ...prevState, ...data })); // Actualizar el estado del modal con los datos guardados
        toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
        // ✅ Solo resetear cuando se crea una nueva integración
        onClose();
        // Usar setTimeout para asegurar que el modal se cierre antes de resetear
        setTimeout(() => {
          setIntegration(initialState);
          setIsLoading(false);
          setLastLoadedId(null);
        }, 100);
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle>
          {integrationId
            ? `${i18n.t("queueIntegrationModal.title.edit")}`
            : `${i18n.t("queueIntegrationModal.title.add")}`}
        </DialogTitle>
        <Formik
          key={`${integrationId || 'new'}-${open}`}
          initialValues={integration}
          enableReinitialize={!isLoading && lastLoadedId === integrationId && !!integrationId}
          validationSchema={Yup.lazy((values) => {
            const currentType = values?.type || integration?.type || "typebot";
            return getValidationSchema(currentType);
          })}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveDialogflow(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <Paper square className={classes.mainPaper} elevation={1}>
                <DialogContent dividers>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="type-selection-input-label">
                          {i18n.t("queueIntegrationModal.form.type")}
                        </InputLabel>

                        <Select
                          label={i18n.t("queueIntegrationModal.form.type")}
                          name="type"
                          labelId="profile-selection-label"
                          value={values.type || ""}
                          onChange={(e) => setFieldValue('type', e.target.value)}
                          error={touched.type && Boolean(errors.type)}
                          id="type"
                          required
                        >
                          <MenuItem value="dialogflow">DialogFlow</MenuItem>
                          <MenuItem value="n8n">N8N</MenuItem>
                          <MenuItem value="webhook">WebHooks</MenuItem>
                          <MenuItem value="typebot">Typebot</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {values.type === "dialogflow" && (
                      <>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            value={values.name || ""}
                            onChange={(e) => setFieldValue('name', e.target.value)}
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel id="language-selection-input-label">
                              {i18n.t("queueIntegrationModal.form.language")}
                            </InputLabel>

                            <Select
                              label={i18n.t("queueIntegrationModal.form.language")}
                              name="language"
                              labelId="profile-selection-label"
                              value={values.language || ""}
                              onChange={(e) => setFieldValue('language', e.target.value)}
                              fullWidth
                              error={touched.language && Boolean(errors.language)}
                              id="language-selection"
                              required
                            >
                              <MenuItem value="pt-BR">Portugués</MenuItem>
                              <MenuItem value="en">Inglés</MenuItem>
                              <MenuItem value="es">Español</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.projectName")}
                            name="projectName"
                            value={values.projectName || ""}
                            onChange={(e) => setFieldValue('projectName', e.target.value)}
                            error={touched.projectName && Boolean(errors.projectName)}
                            helperText={touched.projectName && errors.projectName}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.jsonContent")}
                            type="jsonContent"
                            multiline
                            maxRows={5}
                            minRows={5}
                            fullWidth
                            name="jsonContent"
                            value={values.jsonContent || ""}
                            onChange={(e) => setFieldValue('jsonContent', e.target.value)}
                            error={touched.jsonContent && Boolean(errors.jsonContent)}
                            helperText={touched.jsonContent && errors.jsonContent}
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                      </>
                    )}

                    {(values.type === "n8n" || values.type === "webhook") && (
                      <>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            required
                            name="name"
                            value={values.name || ""}
                            onChange={(e) => setFieldValue('name', e.target.value)}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            value={values.urlN8N || ""}
                            onChange={(e) => setFieldValue('urlN8N', e.target.value)}
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helperText={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                      </>
                    )}
                    {(values.type === "typebot") && (
                      <>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            value={values.name || ""}
                            onChange={(e) => setFieldValue('name', e.target.value)}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            value={values.urlN8N || ""}
                            onChange={(e) => setFieldValue('urlN8N', e.target.value)}
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helperText={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotSlug")}
                            name="typebotSlug"
                            value={values.typebotSlug || ""}
                            onChange={(e) => setFieldValue('typebotSlug', e.target.value)}
                            error={touched.typebotSlug && Boolean(errors.typebotSlug)}
                            helperText={touched.typebotSlug && errors.typebotSlug}
                            required
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotExpires")}
                            name="typebotExpires"
                            value={values.typebotExpires !== undefined && values.typebotExpires !== null ? values.typebotExpires : ""}
                            onChange={(e) => setFieldValue('typebotExpires', e.target.value)}
                            error={touched.typebotExpires && Boolean(errors.typebotExpires)}
                            helperText={touched.typebotExpires && errors.typebotExpires}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotDelayMessage")}
                            name="typebotDelayMessage"
                            value={values.typebotDelayMessage !== undefined && values.typebotDelayMessage !== null ? values.typebotDelayMessage : ""}
                            onChange={(e) => setFieldValue('typebotDelayMessage', e.target.value)}
                            error={touched.typebotDelayMessage && Boolean(errors.typebotDelayMessage)}
                            helperText={touched.typebotDelayMessage && errors.typebotDelayMessage}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotKeywordFinish")}
                            name="typebotKeywordFinish"
                            value={values.typebotKeywordFinish || ""}
                            onChange={(e) => setFieldValue('typebotKeywordFinish', e.target.value)}
                            error={touched.typebotKeywordFinish && Boolean(errors.typebotKeywordFinish)}
                            helperText={touched.typebotKeywordFinish && errors.typebotKeywordFinish}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotKeywordRestart")}
                            name="typebotKeywordRestart"
                            value={values.typebotKeywordRestart || ""}
                            onChange={(e) => setFieldValue('typebotKeywordRestart', e.target.value)}
                            error={touched.typebotKeywordRestart && Boolean(errors.typebotKeywordRestart)}
                            helperText={touched.typebotKeywordRestart && errors.typebotKeywordRestart}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotUnknownMessage")}
                            name="typebotUnknownMessage"
                            value={values.typebotUnknownMessage || ""}
                            onChange={(e) => setFieldValue('typebotUnknownMessage', e.target.value)}
                            error={touched.typebotUnknownMessage && Boolean(errors.typebotUnknownMessage)}
                            helperText={touched.typebotUnknownMessage && errors.typebotUnknownMessage}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12} >
                          <TextField
                            label={i18n.t("queueIntegrationModal.form.typebotRestartMessage")}
                            name="typebotRestartMessage"
                            value={values.typebotRestartMessage || ""}
                            onChange={(e) => setFieldValue('typebotRestartMessage', e.target.value)}
                            error={touched.typebotRestartMessage && Boolean(errors.typebotRestartMessage)}
                            helperText={touched.typebotRestartMessage && errors.typebotRestartMessage}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        
                      </>
                    )}
                  </Grid>
                </DialogContent>
              </Paper>

              <DialogActions>
                {values.type === "dialogflow" && (
                  <Button
                    //type="submit"
                    onClick={(e) => handleTestSession(e, values)}
                    color="inherit"
                    disabled={isSubmitting}
                    name="testSession"
                    variant="outlined"
                    className={classes.btnLeft}
                  >
                    {i18n.t("queueIntegrationModal.buttons.test")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("queueIntegrationModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {integrationId
                    ? `${i18n.t("queueIntegrationModal.buttons.okEdit")}`
                    : `${i18n.t("queueIntegrationModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div >
  );
};

export default QueueIntegration;